import { ModeCanvas } from "@/components/ui/mode-canvas";
import { Mode, WizardModeProps } from "@/modes/mode";
import { ContextualBanner, useBreakpointMdUp } from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/material";
import type { JSX } from "react";
import { useWizardNavigation } from "./use-wizard-navigation";
import { WizardBottomSheet } from "./wizard-bottom-sheet";
import { WizardHeaderBar } from "./wizard-header-bar";
import { WizardStepPanel } from "./wizard-step-panel";

type WizardLayoutProps = {
  /** The wizard mode to render */
  mode: Mode & { wizard: WizardModeProps };
};

/** @returns the full screen layout of a wizard mode: branded header, step panel and the 3D view */
export function WizardLayout({ mode }: WizardLayoutProps): JSX.Element {
  const navigation = useWizardNavigation(mode.name, mode.wizard);
  const { activeStep, isBusy, close } = navigation;
  const isMdUp = useBreakpointMdUp();

  return (
    // The dynamic viewport height keeps the step actions above the browser bars on phones
    <Stack
      sx={{
        height: "100%",
        maxHeight: "100dvh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <WizardHeaderBar
        title={mode.wizard.title}
        disabled={isBusy}
        onClose={close}
      />
      <Stack
        direction={isMdUp ? "row" : "column"}
        sx={{ flexGrow: 1, minHeight: 0 }}
      >
        {isMdUp && <WizardStepPanel {...navigation} />}
        <Box
          component="main"
          // Keeps some 3D view next to the step sheet on small screens
          sx={{
            position: "relative",
            flexGrow: 1,
            minWidth: 0,
            minHeight: "25%",
          }}
        >
          <ModeCanvas modeName={mode.name} />
          {/* On small screens the instructions are in the step sheet, the view needs the room */}
          {activeStep.hint && isMdUp && (
            <ContextualBanner
              sx={{
                position: "absolute",
                top: 16,
                left: "50%",
                transform: "translateX(-50%)",
                maxWidth: "calc(100% - 32px)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {activeStep.hint}
            </ContextualBanner>
          )}
        </Box>
        {!isMdUp && <WizardBottomSheet key={activeStep.key} {...navigation} />}
      </Stack>
    </Stack>
  );
}
